<template>
  <div class="technical-details" data-testid="technical-details">
    <Heading class="technical-details__title" :level="2">Ficha técnica</Heading>
    <div class="technical-details__grid">
      <template v-if="isLoading">
        <SkeletonLoader v-for="index in 4" :key="index">
          <div class="technical-details__row--loader" />
        </SkeletonLoader>
      </template>
      <template v-else>
        <ListDisplay
          v-for="(row, index) in details"
          :key="index"
          class="technical-details__row"
          :title="row.label"
          value=""
          :has-divider="
            index !== (details?.length ?? 0) - 1 &&
            index !== (details?.length ?? 0) - 2
          "
        >
          <template #value-slot>
            <div>
              <span class="technical-details__row__value">{{ row.value }}</span>
              <ButtonIconContextual
                v-if="row.content"
                size="small"
                :label="row.label"
                icon="EF0080"
                @click="toggleDynamicModal(row)"
              />
            </div>
          </template>
        </ListDisplay>
      </template>
    </div>
    <DynamicContentModal
      :is-open="isDynamicContentModalOpen"
      :title="dynamicContentModal.title"
      :modal-info="dynamicContentModal.description"
      :costs-button="dynamicContentModal.showCostsButton"
      @close="toggleDynamicModal"
    />
  </div>
</template>

<script setup lang="ts">
import {
  ButtonIconContextual,
  Heading,
  ListDisplay,
  SkeletonLoader
} from '@warrenbrasil/nebraska-web';
import { PortfolioDetailItem } from '@/modules/wealth/services/portfolio';
import { ref } from 'vue';
import { IDynamicContent } from '@/modules/common/types';
import DynamicContentModal from '@/modules/common/components/DynamicContentModal/DynamicContentModal.vue';

interface Props {
  details?: PortfolioDetailItem[];
  isLoading: boolean;
}
defineProps<Props>();

const isDynamicContentModalOpen = ref(false);
const dynamicContentModal = ref({
  title: '',
  description: [] as IDynamicContent[],
  showCostsButton: false
});

const toggleDynamicModal = (row: PortfolioDetailItem) => {
  if (row) {
    dynamicContentModal.value.title = row.label;
    dynamicContentModal.value.description = row.content;
    dynamicContentModal.value.showCostsButton = !!row.action;
  }
  isDynamicContentModalOpen.value = !isDynamicContentModalOpen.value;
};
</script>

<style lang="less" scoped>
.technical-details {
  &__title {
    margin-bottom: @size-spacing-x400;
  }

  &__row {
    &--loader {
      height: 29px;
      width: 100%;
      border-radius: @size-radius-x100;
      margin: @size-spacing-x350 0;
    }

    &__label {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      word-break: break-all;
      white-space: normal;
    }

    &__value,
    :deep(.list-display__text) {
      white-space: nowrap;
      font-size: 17px;
      color: @element-primary !important;

      div {
        display: flex;
        align-items: center;
      }
    }
  }

  :deep(.list-display) {
    padding: @size-spacing-x350 0;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 0 @size-spacing-x600;

    ::v-deep .container-left {
      flex: auto;
    }

    .to-md({
      grid-template-columns: 1fr;
    });
  }
}
</style>
